// Device Tracking — the estate as three questions, each answered from ONE
// server reply (GetDeviceTrackingList, api/new/deviceTracking.js):
//
//   UNASSIGNED   online now, and no active UserDeviceMapping row — installed
//                and talking, but nobody can see it in an app.
//   OFFLINE      assigned, and not heard from for N days — the "silent since"
//                list, with the threshold the operator picks (60 / 120 / 365).
//   UPDATE       online now, and its flash date is older than its bin file's
//                (or it already carries the RequireUpdateCode mark) — the ones
//                worth sending `updatecode` to right now.
//
// The reply is 1800 rows and half a second, so everything below is CLIENT-side
// and pure: rows in, rows out, no I/O — testable without a server.
//
// ── The facts this reuses, and why it must not re-derive them ─────────────
//   liveness   heartbeatOf / livenessOf (firmwareFleet) — the NetworkConnected
//              stamp first, LastMqttPing second, NEVER the row's own
//              LastUpdatedDateTime (it moves on a rename). 817 of 1811 rows have
//              no NetworkConnected stamp at all and answer from LastMqttPing.
//   firmware   firmwareStateOf (firmwareInfo) — the same tolerance and the same
//              three-valued answer the Firmware page uses, so the two pages
//              cannot disagree about which devices need updating.

import { livenessOf, heartbeatOf, FLEET_LIVE, FLEET_CONCURRENCY } from '../devices/firmwareFleet';
import { firmwareStateOf, STALE, UNKNOWN_STATE } from '../devices/firmwareInfo';
import {
  post, getDeviceTrackingList, getDeviceTypes, getOtaBinFiles, updateDeviceInstalled,
  deleteAllDeviceMappings, getDevicesByRoomFresh, getUserDevicesAtLocation,
  deleteLocationRoomsMapping, deleteUserLocationMapping,
} from '../api/api';
import { setDeviceType, deviceUserIds } from '../devices/deviceAdmin';
import { mapDeviceToBinFile } from '../devices/otaBinFiles';
import { resetDeviceToFresh } from '../devices/freshPiece';

export const VIEW_UNASSIGNED = 'unassigned';
export const VIEW_OFFLINE = 'offline';
export const VIEW_UPDATE = 'update';

export const VIEWS = [
  { key: VIEW_UNASSIGNED, label: 'Live devices — unassigned' },
  { key: VIEW_OFFLINE, label: 'Assigned devices — offline since' },
  { key: VIEW_UPDATE, label: 'Live devices — to be updated' },
];

// "Offline for at least …". 0 = any length of time.
export const OFFLINE_DAYS = [
  { value: 0, label: 'Any' },
  { value: 60, label: '60 days or more' },
  { value: 120, label: '120 days or more' },
  { value: 365, label: '365 days or more' },
];

const DAY_MS = 24 * 60 * 60 * 1000;

const str = (v) => String(v ?? '').trim();

/**
 * One reply row -> one tracking row. Pure.
 *
 * `raw` is kept whole: the control card and the update runner both take the
 * DevicesInstalled shape (UniqueNumber, DeviceID, Status …) and this row IS
 * one, with a few columns added.
 */
export function trackingRowOf(raw, now = Date.now()) {
  const uno = str(raw.UniqueNumber);
  const heartbeat = heartbeatOf(raw);
  const live = livenessOf({ ...raw, NetworkLastUpdatedDateTime: heartbeat }, now);
  const fw = firmwareStateOf(raw);
  const mapped = Number(raw.MappedUsers) || 0;
  return {
    uno,
    deviceName: str(raw.DeviceName) || `Smartify_${uno}`,
    deviceId: raw.DeviceID == null ? null : Number(raw.DeviceID),
    deviceTypeName: str(raw.DeviceTypeName),
    locationId: raw.LocationID == null ? null : Number(raw.LocationID),
    locationName: str(raw.LocationName)
      || (Number(raw.LocationID) ? `Location ${raw.LocationID}` : 'No location'),
    roomName: str(raw.RoomName) || 'Common',
    ownerName: str(raw.OwnerName),
    masterUserId: Number(raw.MasterUserId) || 0,
    mappedUsers: mapped,
    assigned: mapped > 0,
    isLive: live.liveState === FLEET_LIVE,
    lastSeen: live.lastSeen,                     // epoch ms or null
    // Whole days since last heard — null when never heard from at all, which
    // the table shows as "never" rather than as a number.
    offlineDays: live.lastSeen == null
      ? null : Math.floor(Math.max(0, now - live.lastSeen) / DAY_MS),
    firmwareState: fw.state,
    binFile: fw.binFile,
    builtAt: fw.builtAt,
    flashedAt: fw.flashedAt,
    updateMarked: !!fw.updateMarked,
    // Needs it: the flash predates the build, OR the mark is already set (a
    // device somebody has already decided to update, still waiting to be
    // reached). UNKNOWN is deliberately NOT included — a list headed "to be
    // updated" that contained every device with no recorded flash would have
    // the operator reflashing on no evidence.
    needsUpdate: fw.state === STALE || !!fw.updateMarked,
    firmwareUnknown: fw.state === UNKNOWN_STATE,
    raw,
  };
}

/** Rows for one view, BEFORE the type / location / search filters. Pure. */
export function rowsForView(rows, view, offlineDays = 0) {
  const list = rows || [];
  switch (view) {
    case VIEW_UNASSIGNED:
      return list.filter((r) => r.isLive && !r.assigned);
    case VIEW_OFFLINE:
      // "Never heard from" counts as offline for any threshold — it has been
      // silent longer than any number of days.
      return list.filter((r) => r.assigned && !r.isLive
        && (offlineDays <= 0 || r.offlineDays == null || r.offlineDays >= offlineDays));
    case VIEW_UPDATE:
      return list.filter((r) => r.isLive && r.needsUpdate);
    default:
      return list;
  }
}

/** The type / location / search filters, in that order. Pure. */
export function applyFilters(rows, { deviceId = '', locationId = '', search = '' } = {}) {
  let out = rows || [];
  if (deviceId !== '' && deviceId != null) {
    out = out.filter((r) => String(r.deviceId) === String(deviceId));
  }
  if (locationId !== '' && locationId != null) {
    out = out.filter((r) => String(r.locationId) === String(locationId));
  }
  const q = str(search).toLowerCase();
  if (q) {
    out = out.filter((r) => [r.deviceName, r.uno, r.roomName, r.locationName, r.ownerName]
      .some((f) => f && f.toLowerCase().includes(q)));
  }
  return out;
}

/** Distinct device types present in `rows`, by name. */
export function deviceTypeOptions(rows) {
  const seen = new Map();
  (rows || []).forEach((r) => {
    if (r.deviceId == null || seen.has(r.deviceId)) return;
    seen.set(r.deviceId, { deviceId: r.deviceId, label: r.deviceTypeName || `DeviceID ${r.deviceId}` });
  });
  return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Installed devices per type — Map(DeviceID string -> { total, live }). The
 * Device Types page's "Installed" column. Pure.
 */
export function countByType(rows) {
  const out = new Map();
  (rows || []).forEach((r) => {
    if (r.deviceId == null) return;
    const k = String(r.deviceId);
    const c = out.get(k) || { total: 0, live: 0 };
    c.total += 1;
    if (r.isLive) c.live += 1;
    out.set(k, c);
  });
  return out;
}

/** Distinct locations present in `rows`, by name. */
export function locationOptions(rows) {
  const seen = new Map();
  (rows || []).forEach((r) => {
    if (r.locationId == null || seen.has(r.locationId)) return;
    seen.set(r.locationId, { locationId: r.locationId, label: r.locationName });
  });
  return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Counts for the summary chips — all three views at once, so the header can
 * say "312 unassigned · 640 offline · 41 to update" whichever one is open.
 */
export function trackingCounts(rows) {
  const list = rows || [];
  return {
    total: list.length,
    live: list.filter((r) => r.isLive).length,
    unassigned: rowsForView(list, VIEW_UNASSIGNED).length,
    offline: rowsForView(list, VIEW_OFFLINE).length,
    update: rowsForView(list, VIEW_UPDATE).length,
  };
}

export const NOT_DEPLOYED = 'not-deployed';

/** Why the estate list could not be loaded, in words an admin can act on. */
export function trackingErrorText(e) {
  if (!e) return "Couldn't load the installed devices.";
  if (e.code === 'ECONNABORTED' || /timeout/i.test(String(e.message || ''))) {
    return 'The server took too long to list the installed devices (over 2 minutes). '
      + 'The estate query needs the fixed deviceTracking.js deployed and the UniqueNumber '
      + 'columns on utf8mb4 — see uniquenumber_varchar_migration.sql section 5.';
  }
  const r = e.response;
  if (r) {
    const detail = r.data && typeof r.data === 'object' ? (r.data.error || r.data.message || '') : '';
    return `The server answered ${r.status}${detail ? `: ${String(detail).slice(0, 200)}` : ''}.`;
  }
  return `Couldn't reach the server (${String(e.message || 'network error')}).`;
}

/**
 * The whole estate, one request.
 *
 * -> { rows } or { error: NOT_DEPLOYED } when the backend has no such
 * endpoint (an older server answers a missing /api route with an HTML 404, not
 * JSON). Told apart on purpose: "nothing installed" and "the server cannot
 * answer" must not look the same.
 */
export async function loadTracking(now = Date.now()) {
  try {
    const res = await getDeviceTrackingList();
    if (!Array.isArray(res?.data)) return { error: NOT_DEPLOYED, rows: [] };
    return { rows: res.data.map((r) => trackingRowOf(r, now)) };
  } catch (e) {
    const status = e?.response?.status;
    const data = e?.response?.data;
    if (status === 404 || (typeof data === 'string' && /<html/i.test(data))) {
      return { error: NOT_DEPLOYED, rows: [] };
    }
    throw e;
  }
}

// ── Correcting the two facts that are most often wrong ────────────────────
// A device installed under the wrong DeviceID draws the wrong panel and is
// served the wrong firmware; a device whose own OTABinFileId points at the
// wrong build reflashes itself with the wrong image on every "update". Both are
// one column on DevicesInstalled, both are corrected here, one device or many.
//
// ⚠️ SIX AT A TIME, same ceiling as every other estate walk, and one failure
// does not stop the rest — the reply names the unique numbers that failed so
// the operator can retry exactly those.

/** The catalog, for the type dropdown: [{ deviceId, label }] by name. */
export async function loadTypeChoices() {
  const res = await getDeviceTypes();
  const rows = Array.isArray(res?.data) ? res.data : [];
  return rows
    .filter((r) => r.DeviceID != null)
    .map((r) => ({ deviceId: Number(r.DeviceID), label: `${str(r.DeviceName) || 'Unnamed'} · ${r.DeviceID}` }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Every recorded build, for the bin dropdown: [{ otaBinFileId, binFileName }]. */
export async function loadBinChoices() {
  const res = await getOtaBinFiles();
  const rows = Array.isArray(res?.data) ? res.data : [];
  return rows
    .filter((r) => r.OTABinFileId != null && str(r.BinFileName))
    .map((r) => ({ otaBinFileId: Number(r.OTABinFileId), binFileName: str(r.BinFileName) }))
    .sort((a, b) => a.binFileName.localeCompare(b.binFileName));
}

async function eachDevice(unos, fn) {
  const list = [...new Set((unos || []).map(str).filter(Boolean))];
  const failed = [];
  let next = 0;
  const worker = async () => {
    while (next < list.length) {
      const uno = list[next];
      next += 1;
      // eslint-disable-next-line no-await-in-loop
      const ok = await fn(uno).then((r) => r !== false).catch(() => false);
      if (!ok) failed.push(uno);
    }
  };
  await Promise.all(Array.from({ length: Math.min(FLEET_CONCURRENCY, list.length) }, worker));
  return { done: list.length - failed.length, failed };
}

/** DeviceID on each install row. -> { done, failed: [uno] } */
export const setTypeForDevices = (unos, deviceId) => eachDevice(
  unos, (uno) => setDeviceType(uno, deviceId),
);

/**
 * OTABinFileId on each install row, READ BACK through GetDeviceOTAUrlLink so a
 * device that is still served the old image counts as failed, not saved.
 * -> { done, failed: [uno] }
 */
export const setBinForDevices = (unos, otaBinFileId, binFileName) => eachDevice(
  unos, (uno) => mapDeviceToBinFile(uno, otaBinFileId, binFileName).then((r) => r.ok),
);

// ── Unassigning — the answer to "assigned but silent for 977 days" ────────
//
// A device nobody has heard from in years is still mapped to a customer, still
// counted in their room, still drawn on their phone. Unassigning returns it to
// stock: no owner, no location, no room, and NO user mapping at all. The last
// part is what differs from the app's own "remove device" and from
// unassignDeviceFully — both leave the family's mappings in place, and a
// device with an owner of 0 that three users can still see is the
// half-unassigned state this page would then list as "assigned".
//
// ── And the level above: cascade-if-empty ─────────────────────────────────
// A user's room and location mappings are their access to the SITE, shared by
// every device they have there — so they are never removed just because one
// device went. They ARE removed when this run leaves them pointing at nothing:
// no device of the user's left in the room → drop the room mapping; no room
// and no device left at the location → drop the location mapping. Anything
// else is the dangling half-assignment userAccess.danglingUnder exists to count.
//
// The plan is computed FIRST (planUnassign) from what the server says the users
// still have, minus the devices about to go, so the confirm dialog can say
// exactly which room and location mappings will follow — and the run
// (runUnassign) executes the plan rather than re-deciding.
//
// ⚠️ IT IS NOT REVERSIBLE FROM HERE. Re-assigning is the Device Admin page's
// job, one device at a time, with the customer's location and room chosen
// again. Tell the operator that before, not after.
//
// The order is deliberate: mark the users (found through the mappings) → clear
// the install row → delete the device mappings → drop empty rooms → drop empty
// locations. A failure part-way leaves the device at worst owner-less but still
// visible, which the next run repairs — the reverse order could leave it
// invisible but still owned.

const rowsOf = (res) => (Array.isArray(res?.data) ? res.data : []);
const unoOfRow = (d) => str(d?.UniqueNumber ?? d?.uniqueNumber);

/** Devices of `userId` in `roomId` that are NOT in `going`; null when unknown. */
async function remainingInRoom(userId, locationId, roomId, going) {
  const list = await getDevicesByRoomFresh(userId, roomId, locationId).then(rowsOf).catch(() => null);
  if (list == null) return null;                     // unknown → do not drop
  return list.filter((d) => !going.has(unoOfRow(d))).length;
}

/** Devices (any room, or common) plus rooms of `userId` at `locationId`, minus what is going. */
async function remainingAtLocation(userId, locationId, going, roomsDropping) {
  const [devices, rooms] = await Promise.all([
    getUserDevicesAtLocation(userId, locationId).then(rowsOf).catch(() => null),
    post('GetRoomsbyUserIDandLocationId', { UserId: userId, LocationID: locationId })
      .then(rowsOf).catch(() => null),
  ]);
  if (devices == null || rooms == null) return null;   // unknown → do not drop
  const devicesLeft = devices.filter((d) => !going.has(unoOfRow(d))).length;
  const roomsLeft = rooms.filter((r) => !roomsDropping.has(`${userId}:${r.RoomID}`)).length;
  return devicesLeft + roomsLeft;
}

/**
 * What unassigning these rows will do, before it does it. Reads only.
 *
 * -> {
 *   steps:         [{ uno, installedId, locationId, roomId, userIds }]
 *   roomDrops:     [{ userId, locationId, roomId }]   room mappings that would be empty
 *   locationDrops: [{ userId, locationId }]           location mappings that would be empty
 *   usersAffected: number
 * }
 */
export async function planUnassign(rows) {
  const list = (rows || []).filter((r) => r?.uno && r?.raw?.ID != null);
  const going = new Set(list.map((r) => r.uno));

  // Who sees each device — the audience whose rooms and locations may empty.
  const steps = [];
  await eachDevice(list.map((r) => r.uno), async (uno) => {
    const r = list.find((x) => x.uno === uno);
    const userIds = await deviceUserIds(uno);
    const owner = Number(r.raw.MasterUserId) || 0;
    if (owner > 0 && !userIds.includes(owner)) userIds.push(owner);
    steps.push({
      uno,
      installedId: r.raw.ID,
      locationId: Number(r.raw.LocationID) || 0,
      roomId: Number(r.raw.RoomID) || 0,
      userIds,
    });
  });

  // Distinct (user, location, room) and (user, location) touched — a device
  // with no location (already half-unassigned) has nothing above it to empty.
  const roomKeys = new Map();
  const locKeys = new Map();
  steps.forEach((st) => {
    if (!st.locationId) return;
    st.userIds.forEach((userId) => {
      locKeys.set(`${userId}:${st.locationId}`, { userId, locationId: st.locationId });
      if (st.roomId) {
        roomKeys.set(`${userId}:${st.locationId}:${st.roomId}`, { userId, locationId: st.locationId, roomId: st.roomId });
      }
    });
  });

  const roomDrops = [];
  await eachDevice([...roomKeys.keys()], async (k) => {
    const { userId, locationId, roomId } = roomKeys.get(k);
    const left = await remainingInRoom(userId, locationId, roomId, going);
    if (left === 0) roomDrops.push({ userId, locationId, roomId });
  });
  const roomsDropping = new Set(roomDrops.map((d) => `${d.userId}:${d.roomId}`));

  const locationDrops = [];
  await eachDevice([...locKeys.keys()], async (k) => {
    const { userId, locationId } = locKeys.get(k);
    const left = await remainingAtLocation(userId, locationId, going, roomsDropping);
    if (left === 0) locationDrops.push({ userId, locationId });
  });

  const usersAffected = new Set(steps.flatMap((st) => st.userIds)).size;
  return { steps, roomDrops, locationDrops, usersAffected };
}

/**
 * Execute a plan from planUnassign.
 * -> { done, failed: [uno], roomsDropped, locationsDropped, dropFailed }
 */
export async function runUnassign(plan, { userId = null } = {}) {
  const { steps = [], roomDrops = [], locationDrops = [] } = plan || {};
  // uno -> resetDeviceToFresh result. Separate from done/failed: the device IS
  // unassigned once its row and mappings are cleared, and a table the reset
  // could not clear is reported on its own rather than undoing that fact.
  const resets = {};
  const { done, failed } = await eachDevice(steps.map((st) => st.uno), async (uno) => {
    const st = steps.find((x) => x.uno === uno);
    await updateDeviceInstalled(uno, { MasterUserId: 0, LocationID: 0, RoomId: 0 });
    await deleteAllDeviceMappings(st.installedId, uno);
    // Unassigned means a FRESH PIECE — see core/devices/freshPiece.
    resets[uno] = await resetDeviceToFresh(uno, { device: { ID: st.installedId }, userId })
      .catch((e) => ({ ok: false, steps: [{ key: 'reset', label: 'Reset', ok: false, note: String(e) }] }));
    return true;
  });

  // Only the levels above devices that actually went — a device whose row
  // write failed still has its mappings, so its room is not empty after all.
  const goneSteps = steps.filter((st) => !failed.includes(st.uno));
  const touched = (d) => goneSteps.some((st) => st.locationId === d.locationId
    && st.userIds.includes(d.userId) && (d.roomId == null || st.roomId === d.roomId));

  let dropFailed = 0;
  let roomsDropped = 0;
  await eachDevice(roomDrops.filter(touched).map((_, i) => String(i)), async (i) => {
    const d = roomDrops.filter(touched)[Number(i)];
    const ok = await deleteLocationRoomsMapping(d.userId, d.locationId, d.roomId).then(() => true).catch(() => false);
    if (ok) roomsDropped += 1; else dropFailed += 1;
  });
  let locationsDropped = 0;
  await eachDevice(locationDrops.filter(touched).map((_, i) => String(i)), async (i) => {
    const d = locationDrops.filter(touched)[Number(i)];
    const ok = await deleteUserLocationMapping(d.userId, d.locationId).then(() => true).catch(() => false);
    if (ok) locationsDropped += 1; else dropFailed += 1;
  });
  return {
    done, failed, roomsDropped, locationsDropped, dropFailed, resets,
  };
}

/** Plan and run in one go. */
export async function unassignDevices(rows, opts = {}) {
  return runUnassign(await planUnassign(rows), opts);
}
